import { VerificationReport } from "types/landlord/reports";
import { BankStatements, CombinedIncomeReport } from "../../types";
import { IncomeStatus, VerificationStatus } from "shared/types";
import { COMPLETE_VERIFICATION_STATUSES } from "../../constants";
import { Badge, BadgeSize, BadgeVariant } from "shared/components";
import { type Tab } from "shared/components/BaseTabs";
import { Applicant } from "types/application";

export const getFilteredBankStatements = (
  reportData: VerificationReport<CombinedIncomeReport> | undefined
): BankStatements => {
  const data = reportData?.report;
  if (!data?.bank_statements) return {};

  const resetBankStatementIds = new Set(
    reportData?.income_sources
      ?.filter((incomeSource) => incomeSource.status === "reset")
      .map((incomeSource) => incomeSource!.id)
  );

  return Object.fromEntries(
    Object.entries(data.bank_statements).filter(
      ([id]) => !resetBankStatementIds.has(+id)
    )
  ) as BankStatements;
};

export const filterIncomes = (
  allIncomes: CombinedIncomeReport["incomes"],
  verificationStatus: VerificationStatus
) =>
  COMPLETE_VERIFICATION_STATUSES.includes(verificationStatus)
    ? allIncomes.filter((income) => income.status !== IncomeStatus.Pending)
    : allIncomes;

type CombinedIncomeVerification = NonNullable<
  Applicant["combined_income_verifications"]
>[number];

export const getVerificationTabs = (
  verifications: CombinedIncomeVerification[] | undefined
): Tab<number>[] => {
  const chronological = [...(verifications ?? [])]
    .filter((v) => v.status !== VerificationStatus.Canceled)
    .sort((a, b) => {
      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      if (aTime !== bTime) return aTime - bTime;
      return a.id - b.id;
    });

  return chronological
    .map((v, idx) => ({
      value: v.id,
      label: (
        <span className="flex items-center gap-2">
          Report {idx + 1}
          <Badge
            className="flex items-center capitalize"
            size={BadgeSize.SM}
            borderless={false}
            variant={!v.archived_at ? BadgeVariant.SUCCESS : BadgeVariant.BASE}
          >
            {!v.archived_at ? "Active" : "Archived"}
          </Badge>
        </span>
      )
    }))
    .reverse();
};
